import { T } from "../../data/Tokens";

export const Card = ({ children, className = "" }) => (
  <div className={`rounded-2xl bg-white ${className}`}
    style={{ border: `1px solid ${T.border}`, boxShadow: "0 2px 12px rgba(0,0,0,0.05)" }}>
    {children}
  </div>
);

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-0 mb-5 sm:mb-6">
    <div>
      <h1 className="text-[20px] sm:text-[26px]" style={{ color: T.textPri, fontFamily: T.serif, fontWeight: 700 }}>{title}</h1>
      {subtitle && <p className="text-[12px] mt-0.5" style={{ color: T.textSec }}>{subtitle}</p>}
    </div>
    {children && <div className="flex items-center flex-wrap gap-2 w-full sm:w-auto">{children}</div>}
  </div>
);

export const GoldBtn = ({ children, onClick, small }) => (
  <button onClick={onClick}
    className="flex items-center justify-center gap-1.5 rounded-xl font-semibold text-white transition-all hover:shadow-md whitespace-nowrap"
    style={{ background: T.gold, padding: small ? "6px 14px" : "8px 18px", fontSize: small ? 12 : 13 }}>
    {children}
  </button>
);

export const OutlineBtn = ({ children, onClick }) => (
  <button onClick={onClick}
    className="flex items-center justify-center gap-1.5 rounded-xl font-semibold transition-all hover:bg-amber-50 whitespace-nowrap"
    style={{ border: `1.5px solid ${T.gold}`, color: T.gold, padding: "6px 14px", fontSize: 12 }}>
    {children}
  </button>
);

export const GhostBtn = ({ children, onClick }) => (
  <button onClick={onClick}
    className="flex items-center justify-center gap-1.5 rounded-xl font-medium transition-all hover:bg-gray-50 whitespace-nowrap"
    style={{ border: `1px solid ${T.border}`, color: T.textSec, padding: "6px 14px", fontSize: 12 }}>
    {children}
  </button>
);

export const StatusPill = ({ s }) => {
  const map = {
    Completed:  { bg: "#EEFAF0", c: "#4CAF6E" }, Delivered:    { bg: "#EEFAF0", c: "#4CAF6E" },
    Active:     { bg: "#EEFAF0", c: "#4CAF6E" }, Paid:         { bg: "#EEFAF0", c: "#4CAF6E" },
    Approved:   { bg: "#EEFAF0", c: "#4CAF6E" }, Published:    { bg: "#EEFAF0", c: "#4CAF6E" },
    Processing: { bg: "#FFF8EC", c: T.warning  }, "Low Stock":  { bg: "#FFF8EC", c: T.warning },
    Pending:    { bg: "#FFF0F0", c: T.danger   }, Failed:       { bg: "#FFF0F0", c: T.danger  },
    Cancelled:  { bg: "#F5F5F5", c: "#9E9E9E"  }, Archived:    { bg: "#F5F5F5", c: "#9E9E9E" },
    Draft:      { bg: "#F5F5F5", c: "#9E9E9E"  }, Rejected:    { bg: "#FFF0F0", c: T.danger  },
    Shipped:    { bg: "#EEF3FF", c: "#5B7EE5"  }, New:         { bg: "#EEF3FF", c: "#5B7EE5" },
    VIP:        { bg: "#FBF5EA", c: T.gold     }, Inactive:    { bg: "#F5F5F5", c: "#9E9E9E" },
    "Out of Stock": { bg: "#FFF0F0", c: T.danger },
    Unread:     { bg: "#FFF8EC", c: T.warning  }, Read:        { bg: "#F5F5F5", c: "#9E9E9E" },
    Replied:    { bg: "#EEFAF0", c: "#4CAF6E"  }, Scheduled:   { bg: "#EEF3FF", c: "#5B7EE5" },
    Requested:  { bg: "#FFF8EC", c: T.warning  }, Resolved:    { bg: "#EEFAF0", c: "#4CAF6E" },
    Accepted:   { bg: "#EEFAF0", c: "#4CAF6E"  },
  };
  const st = map[s] || { bg: "#F5F5F5", c: "#9E9E9E" };
  return (
    <span className="text-[10px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
      style={{ background: st.bg, color: st.c }}>{s}</span>
  );
};

export const SectionTitle = ({ children }) => (
  <h3 className="font-bold pb-2 mb-4 text-[15px] sm:text-[16px]"
    style={{ color: T.textPri, fontFamily: T.serif, borderBottom: `1px solid ${T.border}` }}>
    {children}
  </h3>
);

export const Field = ({ label, children, required }) => (
  <div>
    <label className="block text-[11px] font-semibold mb-1.5 uppercase tracking-wider" style={{ color: T.textSec }}>
      {label}{required && <span style={{ color: T.danger }}> *</span>}
    </label>
    {children}
  </div>
);

export const Input = ({ value, onChange, placeholder, type = "text" }) => (
  <input type={type} value={value || ""} onChange={onChange} placeholder={placeholder}
    className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none transition-all"
    style={{ background: T.bgSoft, border: `1px solid ${T.border}`, color: T.textPri }}
    onFocus={e => e.target.style.borderColor = T.gold}
    onBlur={e => e.target.style.borderColor = T.border} />
);

export const Textarea = ({ value, onChange, placeholder, rows = 3 }) => (
  <textarea value={value || ""} onChange={onChange} placeholder={placeholder} rows={rows}
    className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none resize-none transition-all"
    style={{ background: T.bgSoft, border: `1px solid ${T.border}`, color: T.textPri }}
    onFocus={e => e.target.style.borderColor = T.gold}
    onBlur={e => e.target.style.borderColor = T.border} />
);

export const Select = ({ value, onChange, children }) => (
  <select value={value || ""} onChange={onChange}
    className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none"
    style={{ background: T.bgSoft, border: `1px solid ${T.border}`, color: T.textPri }}>
    {children}
  </select>
);

export const Toggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer" onClick={() => onChange(!checked)}>
    <div className="relative flex-shrink-0">
      <div className="w-10 h-5 rounded-full transition-colors" style={{ background: checked ? T.gold : T.border }} />
      <div className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all"
        style={{ left: checked ? "22px" : "2px" }} />
    </div>
    <span className="text-[12px]" style={{ color: T.textSec }}>{label}</span>
  </label>
);

export const SearchBar = ({ value, onChange, placeholder = "Search..." }) => (
  <div className="relative w-full sm:w-auto">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
      className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: T.textSec }}>
      <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
    </svg>
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
      className="w-full pl-9 pr-4 py-2.5 text-[12px] rounded-xl outline-none sm:min-w-[220px]"
      style={{ background: "#fff", border: `1px solid ${T.border}`, color: T.textPri }} />
  </div>
);

export const Avatar = ({ name, size = 28 }) => {
  const initials = (name || "??").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="rounded-full flex items-center justify-center font-bold flex-shrink-0"
      style={{ width: size, height: size, background: T.goldL, color: T.goldD,
        border: `1.5px solid ${T.champagne}`, fontSize: size * 0.38 }}>
      {initials}
    </div>
  );
};

export const EmptyState = ({ icon = "📋", title, sub, action, onAction }) => (
  <div className="flex flex-col items-center justify-center py-14 sm:py-20 px-4 text-center">
    <div className="text-4xl sm:text-5xl mb-4">{icon}</div>
    <h3 className="text-[15px] sm:text-[16px] font-bold mb-2" style={{ color: T.textPri, fontFamily: T.serif }}>{title}</h3>
    <p className="text-[12px] max-w-xs mb-5" style={{ color: T.textSec }}>{sub}</p>
    {action && <GoldBtn onClick={onAction}>+ {action}</GoldBtn>}
  </div>
);

export const StatCard = ({ icon, label, value, sub, color, bg }) => (
  <Card className="p-4 sm:p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-pointer">
    <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-2xl flex items-center justify-center text-lg sm:text-xl mb-2.5 sm:mb-3"
      style={{ background: bg || T.goldL }}>{icon}</div>
    <p className="text-[18px] sm:text-[22px] font-bold" style={{ color: T.textPri }}>{value}</p>
    <p className="text-[11px] mt-1" style={{ color: T.textSec }}>{label}</p>
    {sub && <p className="text-[10px] mt-1 font-medium" style={{ color: color || T.success }}>{sub}</p>}
  </Card>
);

export const Th = ({ children }) => (
  <th className="px-2.5 sm:px-4 py-2.5 sm:py-3 text-left text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap"
    style={{ color: T.textSec }}>{children}</th>
);

export const Td = ({ children, className = "" }) => (
  <td className={`px-2.5 sm:px-4 py-3 sm:py-3.5 ${className}`}>{children}</td>
);

export const Tr = ({ children, last }) => (
  <tr className="transition-colors"
    style={{ borderBottom: last ? "none" : `1px solid ${T.bgSoft}` }}
    onMouseEnter={e => e.currentTarget.style.background = T.bgSoft}
    onMouseLeave={e => e.currentTarget.style.background = ""}>
    {children}
  </tr>
);

// Wraps a <table> so wide tables scroll horizontally on small screens
// instead of breaking the page layout. Use as: <TableScroll><table>...</table></TableScroll>
export const TableScroll = ({ children }) => (
  <div className="overflow-x-auto -mx-px" style={{ WebkitOverflowScrolling: "touch" }}>
    <div className="min-w-[640px] sm:min-w-0">{children}</div>
  </div>
);

export const Tabs = ({ tabs, active, onChange }) => (
  <div className="flex gap-1 p-1 rounded-2xl w-full sm:w-fit mb-5 overflow-x-auto"
    style={{ background: T.bgSoft, scrollbarWidth: "none" }}>
    {tabs.map(t => (
      <button key={t.id} onClick={() => onChange(t.id)}
        className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-xl text-[12px] font-medium transition-all whitespace-nowrap flex-shrink-0"
        style={active === t.id
          ? { background: "white", color: T.gold, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" }
          : { color: T.textSec }}>
        {t.label}
        {t.count != null && (
          <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
            style={{ background: active === t.id ? T.goldL : T.border,
              color: active === t.id ? T.goldD : T.textSec }}>
            {t.count}
          </span>
        )}
      </button>
    ))}
  </div>
);

export const ConfirmModal = ({ title, message, onConfirm, onCancel, danger = true }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background: "rgba(0,0,0,0.5)" }}>
    <div className="rounded-3xl p-6 sm:p-8 w-full max-w-xs text-center" style={{ background: T.card }}>
      <div className="text-4xl mb-3">{danger ? "🗑️" : "⚠️"}</div>
      <h3 className="text-[16px] font-bold mb-2" style={{ color: T.textPri, fontFamily: T.serif }}>{title}</h3>
      <p className="text-[12px] mb-6" style={{ color: T.textSec }}>{message}</p>
      <div className="flex gap-3">
        <button onClick={onCancel}
          className="flex-1 py-2.5 rounded-xl text-[13px] font-medium border"
          style={{ borderColor: T.border, color: T.textSec }}>Cancel</button>
        <button onClick={onConfirm}
          className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold text-white"
          style={{ background: danger ? T.danger : T.gold }}>Confirm</button>
      </div>
    </div>
  </div>
);